import React from 'react';
import { Drawer, List, ListItem, ListItemText, ListItemIcon, Divider, Toolbar, Box, Typography } from '@mui/material';
import { Link, useLocation } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';

const CATEGORIES = [
  { id: 'Preprocessing', labelEn: 'Preprocessing', labelTr: 'Önişleme' },
  { id: 'Morphology', labelEn: 'Morphology', labelTr: 'Morfoloji' },
  { id: 'Syntax', labelEn: 'Syntax', labelTr: 'Sözdizim' },
  { id: 'Analysis', labelEn: 'Analysis & Semantics', labelTr: 'Analiz ve Anlambilim' },
  { id: 'Generation', labelEn: 'Text Generation', labelTr: 'Metin Üretimi' }
];

const getCategory = (toolEnum) => {
  const mapping = {
    'Preprocessing': ['tokenizer', 'sentence-splitter', 'deasciifier', 'deasciifier_v2', 'sent_splitter_v2', 'tokenizer_version2'],
    'Morphology': ['lemmatizer', 'morphological_analyzer', 'morphological_disambiguator', 'morphological_analyzer_disambiguator'],
    'Syntax': ['boun_pars'],
    'Analysis': ['ermi', 'ner', 'question_answering', 'relx', 'sentiment_embeddings', 'bounti_sentiment_analyzer', 'targeted-sentiment-analysis', 'trvectors', 'postagger', 'spellcorrection', 'vowelizer'],
    'Generation': ['bounsumm']
  };
  for (const [cat, enums] of Object.entries(mapping)) {
    if (enums.includes(toolEnum)) return cat;
  }
  return 'General';
};

export default function MyDrawer({ tools, mobileOpen, handleDrawerToggle, drawerWidth }) {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const drawerContent = (
    <Box sx={{ bgcolor: '#ffffff', height: '100%' }}>
      <Toolbar sx={{ minHeight: '64px !important' }} />
      <List sx={{ pt: 2 }}>
        {location.pathname !== "/" && (
          <>
            <ListItem 
              button 
              component={Link} 
              to="/"
              sx={{ 
                mb: 1,
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': { bgcolor: 'primary.light', color: 'white' }
              }}
            >
              <ListItemIcon sx={{ minWidth: '40px' }}>
                <ArrowBackIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={t('homepage')} 
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} 
              />
            </ListItem>
            <Divider sx={{ my: 2, mx: 2 }} />
          </>
        )}

        {CATEGORIES.map(cat => {
          const toolsInCategory = tools.filter(tool => getCategory(tool.enum) === cat.id);
          if (toolsInCategory.length === 0) return null;

          return (
            <Box key={cat.id} sx={{ mb: 2 }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  px: 3, 
                  fontWeight: 800, 
                  color: 'text.disabled',
                  letterSpacing: '0.1em'
                }}
              >
                {lang === 'tr' ? cat.labelTr : cat.labelEn}
              </Typography>
              {toolsInCategory.map((tool) => (
                <ListItem 
                  button 
                  key={tool.enum}
                  component={Link} 
                  to={'/' + tool.enum}
                  selected={location.pathname === '/' + tool.enum}
                  sx={{ 
                    mx: 1,
                    my: 0.2,
                    borderRadius: 2,
                    '&.Mui-selected': { 
                      bgcolor: '#e6f0ff', 
                      color: '#003366',
                      '&:hover': { bgcolor: '#d9eaff' }
                    },
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ListItemText 
                    primary={tool.general_info[lang].name} 
                    primaryTypographyProps={{ 
                      fontSize: '0.875rem', 
                      fontWeight: location.pathname === '/' + tool.enum ? 700 : 500 
                    }} 
                  />
                </ListItem>
              ))}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e0e0e0' },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e0e0e0' },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}
